/* G3D custom (2026-10-15): the Mystery 3-in-1 Bundle product page (sections/g3d-mystery-bundle.liquid), "The lucky draw".
   Ported from the approved, debloated mockup (Obsidian vault: Website/mockups/mystery-bundle-a.html, the drum rework after phase 2,
   MD5 d66fec4d). The product, its variant, price and the example balls (names, types, photos) come from the #mb-data block the section
   renders; the type palette, the draw and the drum's Draw spin stay here, as in the mockup. Add to cart posts the variant and quantity
   to the theme's cart add route and fires CartLinesUpdateEvent, like the theme's own product form, so the header bubble and the cart
   drawer refresh. Module script: nothing lands on window. */
import { ProductViewEvent } from '@shopify/events';
import { addToCart } from '@theme/g3d-cart';
import { formatMoney } from '@theme/money-formatting';

(() => {
  const $ = id => document.getElementById(id), root = $('mb-root');
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)'); // .matches at each use: it can change mid-visit
  // the product's variant (id, price in cents), its title, the shop's money format and the example balls: rendered by the section
  const DATA = JSON.parse($('mb-data').textContent);
  const TYPE_C = { Normal: '#8f99a1', Fighting: '#ce3f6b', Flying: '#92aade', Poison: '#ab6ac9', Ground: '#da7747', Rock: '#c8b78a', Bug: '#91c02c', Ghost: '#5369ad', Steel: '#5b8fa2',
    Fire: '#fc9c53', Water: '#4d90d6', Grass: '#66bc5f', Electric: '#f4d23b', Psychic: '#f97077', Ice: '#73cebf', Dragon: '#096dc3', Dark: '#5b5265', Fairy: '#ec90e8' };
  // the examples collection minus ponchos: n = title, t = types, f / s = front and shiny-front photos (360px; '' when there is none)
  const POOL = DATA.balls.map(({ n, t, f, s }) => { const ty = t || []; return { n: n.replace(/ Ball$/, ''), f, s, c: TYPE_C[ty[0]] || '#8b8d98', c2: TYPE_C[ty[1]] || TYPE_C[ty[0]] || '#8b8d98' }; });
  const money = c => formatMoney(c, DATA.moneyFormat || '${{amount}}', DATA.currency || 'USD'); // as Liquid's | money
  const list = a => a.slice(0, -1).join(', ') + ' and ' + a.at(-1);
  const wait = ms => new Promise(r => setTimeout(r, ms));
  const replay = (el, cls) => { el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls); };

  /* ---- state ---- */
  let hand = [], up = false, busy = false; // hand: three { b: ball, sh: shown Shiny }
  const slots = [...root.querySelectorAll('.mb-slot')], drawBtn = $('mb-draw'), said = $('mb-said');
  const photo = h => h.sh ? h.b.s : h.b.f;

  /* ---- the draw ---- */
  const rand = a => a[Math.random() * a.length | 0];
  // 3 different balls, none already on the table. Each card may come up Shiny (about 2 draws in 5 show one), and only as a ball we
  // have a real shiny photo of, so a Shiny is never faked (Haxorus has no normal photo, so it only comes up Shiny).
  const SHINY_ODDS = .16; // per card
  function pick3() {
    const pool = POOL.filter(b => !hand.some(h => h.b === b)), out = [], free = b => !out.some(h => h.b === b);
    for (let k = 0; k < 3; k++) {
      const shinies = pool.filter(b => b.s && free(b));
      if (shinies.length && Math.random() < SHINY_ODDS) { out.push({ b: rand(shinies), sh: true }); continue; }
      let b; do b = rand(pool); while (!b.f || !free(b));
      out.push({ b, sh: false });
    }
    return out;
  }
  // one card's look: face down, or its example ball (gold frame, shiny photo and "✦ Shiny" when it was drawn Shiny)
  function paint(k, sweep) {
    const c = slots[k].firstElementChild, sr = c.querySelector('.mb-sr');
    c.classList.toggle('is-up', up);
    if (!up) { sr.textContent = `Card ${k + 1}, face down`; return; }
    const { b, sh } = hand[k];
    c.style.setProperty('--c', b.c); c.style.setProperty('--c2', b.c2);
    c.classList.toggle('is-shiny', sh);
    c.querySelector('.mb-card__top b').textContent = b.n;
    c.querySelector('img').src = photo(hand[k]);
    c.querySelector('.mb-card__foot').lastElementChild.textContent = sh ? '✦ Shiny' : 'Normal';
    sr.textContent = `Card ${k + 1}: example, ${sh ? 'Shiny ' : ''}${b.n} Ball`;
    // the face is display:none while face down, so a kept is-swept would replay the sweep on every later draw: clear it unless Shiny
    if (sweep && sh && !REDUCED.matches) replay(c, 'is-swept'); else c.classList.remove('is-swept');
  }
  // the Home page's card flip, transform only. The card is single-faced: it steps to scaleX(-1) while edge-on (ending on no
  // transform) and swaps its face in the frame the turn passes 90deg (21.47% of the time on that curve).
  const EDGE = 98.78, STAGGER = 120;
  function turn(k, swap) {
    const slot = slots[k], c = slot.firstElementChild, delay = k * STAGGER;
    if (REDUCED.matches || !c.animate) { swap(); return Promise.resolve(); }
    slot.animate([{ transform: 'none' }, { transform: 'translateY(-8px) scale(1.035)', offset: .3 }, { transform: 'none' }], { duration: 460, delay, easing: 'ease-out' });
    const r = 'perspective(1400px) rotateY', t = c.animate([{ transform: r + '(0deg)' }, { transform: r + '(90deg)', offset: .5 }, { transform: r + '(90deg) scaleX(-1)', offset: .5 },
      { transform: r + '(180deg) scaleX(-1)' }], { duration: 460, delay, easing: 'cubic-bezier(.3,.6,.25,1)' });
    let swapped = false;
    const edge = () => { if (!swapped) { swapped = true; swap(); } };
    (function tick() { t.currentTime == null || t.currentTime >= delay + EDGE ? edge() : requestAnimationFrame(tick); })();
    return t.finished.then(edge, edge);
  }
  const turnAll = sweep => Promise.all(slots.map((s, k) => turn(k, () => paint(k, sweep))));
  const load = src => new Promise(r => { const i = new Image(); i.onload = i.onerror = r; i.src = src; });
  const preload = hs => Promise.race([Promise.all(hs.map(h => load(photo(h)))), wait(2500)]);
  async function draw() {
    if (busy) return; // taps while the cards turn are ignored (the button is aria-disabled then)
    busy = true;
    drawBtn.setAttribute('aria-disabled', 'true');
    $('mb-drawL').textContent = 'Drawing…'; // the loading state: a first draw can wait up to 2.5 s for its photos
    try { spin(); } catch {} // decorative: it must never leave the button disabled
    const next = pick3(), ready = preload(next);
    if (up) { up = false; await turnAll(); await wait(140); } // face down first, so a new draw reads as a new draw
    await ready;
    hand = next; up = true;
    await turnAll(true);
    said.textContent = `Examples: ${list(hand.map(h => (h.sh ? 'Shiny ' : '') + h.b.n))}. Yours are picked at random when we pack, Shiny or not.`;
    $('mb-drawL').textContent = 'Draw 3 more';
    busy = false;
    drawBtn.removeAttribute('aria-disabled');
  }
  if (drawBtn) drawBtn.addEventListener('click', draw); // the section leaves the button out when the collection has too few balls
  // the drum's Draw spin: every turning part takes one more turn in 1300 ms (ease in-out, added on top of the idle turn, which
  // runs on); the near copies' opacity follows the same angle and the tickets tumble up the front wall and settle
  const drum = $('mb-drum'), NEAR = [114.5, 117.5, 294.5, 297.5], ease = x => x < .5 ? 4 * x ** 3 : 1 - (2 - 2 * x) ** 3 / 2;
  const near = d => { d = (d % 360 + 360) % 360; const [a, b, c, e] = NEAR; return d <= a || d >= e ? 0 : d < b ? (d - a) / (b - a) : d <= c ? 1 : (e - d) / (e - c); };
  const F = Array.from({ length: 41 }, (_, i) => i / 40), rot = s => F.map(f => ({ transform: `rotate(${s * 360 * ease(f)}deg)` })), FW = rot(1), BW = rot(-1);
  let kick;
  function spin() { // (no composite 'add', as in older Safari: the cage would snap to 0deg, so no spin)
    if (REDUCED.matches || !drum.animate || !('composite' in KeyframeEffect.prototype) || drum.classList.contains('is-paused')) return;
    if (kick?.playState === 'running') { kick.finished.then(spin); return; } // a draw right after the last: spin as its cards deal
    const t = { duration: 1300 }, all = s => drum.querySelectorAll(s);
    const drift = getComputedStyle(all('.mb-spin')[0]).animationPlayState === 'paused' ? 0 : 11.1429; // phones, touch tablets: no idle turn
    all('.mb-nr').forEach(g => { // its angle now, then where the kick and the idle (42 s a turn) take it (a keyframe equal to both
      const a0 = 360 * (g.getAnimations()[0]?.effect.getComputedTiming().progress || 0); // neighbours adds nothing: ~10 of 41 are kept)
      g.animate(F.map(f => ({ opacity: near(a0 + 360 * ease(f) + drift * f), offset: f })).filter((k, i, K) => !(i % 40 && k.opacity === K[i - 1].opacity && k.opacity === K[i + 1].opacity)), t);
    });
    // the door's sheet is a sampled shape on the idle clock, so it can't take the added turn: it ducks out while the cage whirls
    all('.mb-ds').forEach(g => g.animate([{ opacity: 1 }, { opacity: 0, offset: .1 }, { opacity: 0, offset: .9 }, { opacity: 1 }], t));
    all('.mb-spin').forEach(g => kick = g.animate(FW, { ...t, composite: 'add' }));
    all('.mb-pin').forEach(g => g.animate(BW, { ...t, composite: 'add' }));
    all('.mb-tt').forEach((g, i) => { // each ticket turns about its own centre (its plane's origin)
      const { e, f } = g.firstElementChild.transform.baseVal.consolidate().matrix, h = 14 + i * 7 % 17, r = (i % 2 ? 1 : -1) * (6 + i * 5 % 15);
      const at = (x, y, a) => ({ transform: `translate(${e + x}px, ${f + y}px) rotate(${a}deg) translate(${-e}px, ${-f}px)` });
      g.animate([at(0, 0, 0), { ...at(-h / 2, -h, r), offset: .42 }, { ...at(-h / 10, 1.5, r / 4), offset: .78 }, at(0, 0, 0)],
        { duration: 1200, delay: i % 5 * 25, easing: 'ease-in-out' });
    });
  }
  // the drum's idle loops pause off screen. The default root clips through every scroller (the theme's .page-wrapper from 990px up too)
  if (window.IntersectionObserver) new IntersectionObserver(es => es.forEach(e => e.target.classList.toggle('is-paused', !e.isIntersecting))).observe(drum);
  // "Pause motion" (WCAG 2.2.2): .is-still holds the backdrop's drift and the drum's idle turn (the CSS); the label carries the state
  const motion = $('mb-motion');
  motion.addEventListener('click', () => { motion.textContent = root.classList.toggle('is-still') ? 'Play motion' : 'Pause motion'; });
  root.classList.add('mb-ready'); // the section's fallback script checks this on load

  /* ---- quantity ---- */
  const qIn = $('mb-q'), qty = () => Math.min(99, Math.max(1, parseInt(qIn.value, 10) || 1));
  function syncQty() {
    const q = qty();
    qIn.value = q;
    // aria-disabled, not disabled: disabling the focused button would drop keyboard focus to <body> (qty() clamps the clicks)
    $('mb-less').setAttribute('aria-disabled', q <= 1);
    $('mb-more').setAttribute('aria-disabled', q >= 99);
    if (!$('mb-add').classList.contains('is-added')) $('mb-addS').textContent = money(DATA.price * q);
  }
  $('mb-less').addEventListener('click', () => { qIn.value = qty() - 1; syncQty(); });
  $('mb-more').addEventListener('click', () => { qIn.value = qty() + 1; syncQty(); });
  qIn.addEventListener('change', syncQty);

  /* ---- add to cart ---- */
  let adding = false;
  $('mb-add').addEventListener('click', async () => {
    const add = $('mb-add');
    if (adding) return;
    adding = true;
    said.textContent = ''; // so a repeat of the same message is announced again
    const q = qty();
    $('mb-addL').textContent = 'Adding…';
    $('mb-err').hidden = true;
    try {
      await addToCart(add, [{ id: DATA.variant, quantity: q }], 'g3d-mystery-bundle');
    } catch (err) {
      adding = false;
      $('mb-addL').textContent = 'Add to cart';
      // shown under the button and announced (the live region is visually hidden)
      said.textContent = $('mb-err').textContent = "Couldn't add it to your cart: " + (err.message || 'please try again').replace(/\.$/, '') + '.';
      $('mb-err').hidden = false;
      return;
    }
    add.classList.add('is-added');
    $('mb-addL').innerHTML = 'Added to cart <span aria-hidden="true">✓</span>';
    said.textContent = `Added ${q} ${DATA.title}${q > 1 ? 's' : ''} to your cart.`;
    setTimeout(() => { add.classList.remove('is-added'); $('mb-addL').textContent = 'Add to cart'; adding = false; syncQty(); }, 2600);
  });

  syncQty();

  // the stock product section reports the product view; this page replaces that section, so it reports it instead (as the Starter and Legendary pages do)
  try {
    const view = JSON.parse(root.dataset.mbView);
    view.context = view.context || 'page';
    setTimeout(() => root.dispatchEvent(new ProductViewEvent(view)));
  } catch (err) { console.error(err); }
})();
